import { STATUSES } from "~/constants/reservation_statuses"
import { useGuest } from "./useGuest"
import Button from "~/components/ui/Button"
import {
  CreditCardIcon,
  EllipsisHorizontalIcon,
  EnvelopeIcon,
  EyeIcon,
  PencilIcon,
  UserIcon,
  UserMinusIcon,
  UserPlusIcon,
} from "@heroicons/react/24/solid"
import DropdownButton, { DropdownOption } from "~/components/ui/Admin/DropdownButton"
import ProfileCard from "../../../Campers/Camper/ProfileCard"
import ICamper from "~/interfaces/ICamper"
import { AdminContext } from "~/contexts/AdminContext"
import { useContext } from "react"
import { ROUTER_PATHS } from "~/routes/constants"

type Props = {
  setSlideoverOpen: (v: boolean) => void
  isLoading: boolean
}

export const Guest: React.FC<Props> = (props) => {
  const { selectedReservation, reservationStates, selectedCamper: constextSelectedCamper } = useContext(AdminContext)
  const selectedCamper = selectedReservation?.camper

  const {
    onClickEditCamper,
    camper,
    showMessageModal,
    setSelectedCamper,
    setCurrentPage,
    detachCamperMutation,
    isLoading: isGuestLoading,
    handleSavePaymentMethod,
  } = useGuest({
    setSlideoverOpen: props.setSlideoverOpen,
    selectedCamper,
  })
  if (selectedReservation?.status === STATUSES.BLOCKED) return null

  const options: DropdownOption[] = []

  if (camper.data?.camper && camper.data.camper.id && !camper.isLoading) {
    const currentCamper: ICamper = camper.data.camper
    options.push(
      {
        label: "Edit guest",
        icon: PencilIcon,
        onClick: () => onClickEditCamper(),
      },
      {
        label: "View guest",
        icon: EyeIcon,
        onClick: () => {
          setSelectedCamper(currentCamper)
          if (currentCamper?.id) window.location.href = `${ROUTER_PATHS.ADMIN_GUESTS}/${currentCamper?.id}`
        },
      },
      {
        label: "Detach guest",
        icon: UserMinusIcon,
        onClick: (e) => detachCamperMutation(),
      },
      {
        label: "_divider_",
      },
      {
        label: "Add payment method",
        icon: CreditCardIcon,
        onClick: handleSavePaymentMethod,
        disabled: !camper.data?.camper?.email && !camper.data?.camper?.phone_number,
        tooltip:
          !camper.data?.camper?.email &&
          !camper.data?.camper?.phone_number &&
          "Please add either an email or phone number to save payment methods",
      },
    )
  } else {
    options.push({
      label: "Add guest",
      icon: UserPlusIcon,
      onClick: () => onClickEditCamper(true),
    })
  }
  options.push(
    {
      label: "_divider_",
    },
    {
      label: "Message guest",
      icon: EnvelopeIcon,
      onClick: () => {
        showMessageModal({ reservations: [selectedReservation] })
        props.setSlideoverOpen(false)
      },
    },
  )

  return (
    <>
      <div className="flex items-center w-full justify-between mb-2 mt-6">
        <div className="font-bold text-xl">Guest</div>
        <div className="flex flex-wrap justify-end">
          {!camper.isLoading &&
            (camper?.data?.camper?.id ? (
              <Button
                type="button"
                variant="white"
                size="small"
                shadow="small"
                className="flex items-center mr-2 text-gray-600"
                disabled={camper.isLoading}
                onClick={() => onClickEditCamper()}
                dataTestId="edit-guest"
              >
                <UserIcon className="w-5 h-5 mr-2" />
                <div className="hidden sm:block">Edit guest</div>
                <div className="sm:hidden">Edit</div>
              </Button>
            ) : (
              <Button
                type="button"
                variant="white"
                size="small"
                shadow="small"
                className="flex items-center mr-2 text-gray-600"
                disabled={camper.isLoading}
                onClick={() => onClickEditCamper()}
                dataTestId="add-guest"
              >
                <UserIcon className="w-5 h-5 mr-2" />
                <div className="hidden sm:block">Add guest</div>
                <div className="sm:hidden">Add</div>
              </Button>
            ))}
          {(selectedReservation?.camper?.email || selectedReservation?.camper?.phone) && (
            <Button
              type="button"
              variant="white"
              size="small"
              shadow="small"
              className="flex items-center text-gray-600 mr-2"
              onClick={() => {
                showMessageModal({ reservations: [selectedReservation] })
                props.setSlideoverOpen(false)
              }}
              dataTestId="message-guest"
            >
              <EnvelopeIcon className="w-5 h-5 mr-2" />
              <div className="hidden sm:block">Send message</div>
              <div className="sm:hidden">Message</div>
            </Button>
          )}
          <DropdownButton disabled={isGuestLoading} options={options}>
            <Button
              variant="white"
              disabled={props.isLoading || isGuestLoading}
              className="flex items-center text-gray-600"
              size="small"
              shadow="small"
              type="button"
              style={{ height: 35 }}
              dataTestId="more-guest-options"
            >
              <EllipsisHorizontalIcon className="h-6 w-6 text-gray-600" aria-hidden="true" />
            </Button>
          </DropdownButton>
        </div>
      </div>
      <div className="pb-12 relative">
        <ProfileCard
          camper={
            camper.data?.camper || selectedCamper || selectedReservation?.camper || selectedReservation?.user?.camper
          }
          isBlocked={camper?.data?.camper?.isBlocked}
          isCamperLoading={camper.isLoading}
          paymentMethods={camper?.data?.camper?.payment_methods}
        />
      </div>
    </>
  )
}
